import dayjs from 'dayjs';

interface StickerAvailabilityInput {
  title?: string | null;
  status?: string | null;
  campaignType?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  sweepstakesSticker?: {
    drawingDateTime?: string | null;
    drawnAt?: string | null;
  } | null;
}

/**
 * Why a sticker can't be bought / entered right now, or null when it can.
 * Mirrors the backend purchase gate (StickerAvailability.ClosedReason, SPO-628) and uses the same
 * buyer-facing copy, so the page can hide its buy CTA instead of waiting for the mutation to fail.
 * The backend stays the source of truth (it also closes events once their day is over).
 */
export default function getStickerClosedReason(
  sticker: StickerAvailabilityInput | null | undefined,
  now = dayjs(),
): string | null {
  if (!sticker) return null;

  const isSweepstakes = sticker.campaignType?.toLowerCase() === 'sweepstakes';
  const fallbackTitle = isSweepstakes ? 'This sweepstakes' : 'This sticker';
  const title = sticker.title?.trim() ? `'${sticker.title.trim()}'` : fallbackTitle;
  const ended = isSweepstakes
    ? `${title} has ended and is no longer accepting entries.`
    : `${title} has ended and is no longer available.`;

  if (sticker.status === 'Archived') return ended;
  // A missing status only happens on legacy rows, which the backend treats as Active.
  if (sticker.status && sticker.status !== 'Active') {
    return `${title} is not available for purchase.`;
  }
  if (sticker.startDate && dayjs(sticker.startDate).isAfter(now)) {
    return `${title} is not available yet.`;
  }
  if (sticker.endDate && dayjs(sticker.endDate).isBefore(now)) return ended;

  const sweepstakes = sticker.sweepstakesSticker;
  if (isSweepstakes && sweepstakes) {
    if (sweepstakes.drawnAt) {
      return `${title} has already drawn its winners and is no longer accepting entries.`;
    }
    if (sweepstakes.drawingDateTime && !dayjs(sweepstakes.drawingDateTime).isAfter(now)) {
      return `Entries for ${title} closed at the drawing time and can no longer be accepted.`;
    }
  }

  return null;
}
